import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { subscriptionsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const SubscriptionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchSubscription(); }, [id]);

  const fetchSubscription = async () => {
    try {
      const res = await subscriptionsAPI.getById(id);
      setSubscription(res.data);
    } catch (error) {
      toast.error('Gagal memuat data');
      navigate('/subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Yakin hapus langganan ini?')) {
      try {
        await subscriptionsAPI.delete(id);
        toast.success('Berhasil dihapus');
        navigate('/subscriptions');
      } catch (error) {
        toast.error('Gagal menghapus');
      }
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
    </div>
  );

  if (!subscription) return null;

  const daysUntilExpiry = Math.ceil((new Date(subscription.expiryDate) - new Date()) / (1000 * 60 * 60 * 24));

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Detail Langganan</h1>
        {isAdmin && (
          <div className="flex gap-2">
            <Link to={`/subscriptions/${id}/edit`} className="btn-secondary text-sm">Edit</Link>
            <button onClick={handleDelete} className="btn-danger text-sm">Hapus</button>
          </div>
        )}
      </div>

      <div className="card space-y-4 sm:space-y-6">
        <div className="flex items-center justify-between pb-4 border-b">
          <div className="min-w-0">
            <h2 className="text-lg sm:text-xl font-semibold truncate">{subscription.zoomAccountEmail}</h2>
            <p className="text-gray-500 text-sm">ID: {subscription.zoomAccountId || '-'}</p>
          </div>
          <StatusBadge status={subscription.status} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          <InfoItem label="User" value={subscription.user?.name} sub={subscription.user?.department} />
          <InfoItem label="Email User" value={subscription.user?.email} />
          <InfoItem label="Tanggal Beli" value={new Date(subscription.purchaseDate).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })} />
          <InfoItem label="Tanggal Kedaluwarsa" value={new Date(subscription.expiryDate).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })} />
          <InfoItem label="Durasi" value={`${subscription.durationMonths} bulan`} />
          <InfoItem label="Tipe Paket" value={subscription.planType || '-'} />
          <InfoItem label="Harga" value={`Rp ${subscription.purchasePrice?.toLocaleString('id-ID') || '-'}`} />
          <div>
            <p className="text-sm text-gray-500 mb-1">Sisa Waktu</p>
            <p className={`font-medium ${daysUntilExpiry <= 7 ? 'text-red-600' : daysUntilExpiry <= 30 ? 'text-yellow-600' : 'text-green-600'}`}>
              {daysUntilExpiry > 0 ? `${daysUntilExpiry} hari` : 'Sudah berakhir'}
            </p>
          </div>
        </div>

        {subscription.notes && (
          <div className="pt-4 border-t">
            <p className="text-sm text-gray-500 mb-1">Catatan</p>
            <p className="text-gray-700 text-sm">{subscription.notes}</p>
          </div>
        )}

        <div className="pt-4 border-t">
          <p className="text-xs text-gray-500">Terakhir diperbarui: {new Date(subscription.updatedAt).toLocaleString('id-ID')}</p>
        </div>
      </div>

      <div className="mt-4 sm:mt-6">
        <Link to="/subscriptions" className="text-primary-600 hover:text-primary-700 text-sm">
          ← Kembali
        </Link>
      </div>
    </div>
  );
};

const InfoItem = ({ label, value, sub }) => (
  <div>
    <p className="text-sm text-gray-500 mb-1">{label}</p>
    <p className="font-medium text-sm sm:text-base">{value}</p>
    {sub && <p className="text-sm text-gray-500">{sub}</p>}
  </div>
);

const StatusBadge = ({ status }) => {
  const styles = { active: 'bg-green-100 text-green-800', expiring_soon: 'bg-yellow-100 text-yellow-800', expired: 'bg-red-100 text-red-800', cancelled: 'bg-gray-100 text-gray-800' };
  const labels = { active: 'Aktif', expiring_soon: 'Akan Berakhir', expired: 'Berakhir', cancelled: 'Batal' };
  return <span className={`px-2 sm:px-3 py-1 text-xs sm:text-sm font-medium rounded-full ${styles[status]}`}>{labels[status]}</span>;
};

export default SubscriptionDetail;
